import { useCallback } from 'react';

import { useVideoPlatformCallback } from '../../../hooks/useVideoPlatform';
import { AppDependencies } from '../../../state/AppDependenciesContext';
import { useAppDependenciesContext } from '../../../state/useAppDependenciesContext';
import { Stopwatch } from '../../../utils';
import { useCoerceVideoPlatform as useBaseCoerceVideoPlatform } from '../../../video-sdk/actions/useLifecycleActions';
import { VideoPlatform, normalizeVideoPlatform, videoPlatformsMatch } from '../../../video-sdk/videoPlatform';
import { useGetVideoPlatformOfAdmittedRoomCallback } from './useVideoPlatformOfAdmittedRoom';

type LogContext = {
  fromPlatform: VideoPlatform;
  toPlatform: VideoPlatform | null;
  durationMs?: number;
};

type CoerceVisitorVideoPlatformResult = readonly [true, 'noop' | VideoPlatform] | readonly [false, Error];

function logResult({
  result,
  logContext,
  logger,
}: {
  result: CoerceVisitorVideoPlatformResult;
  logContext: LogContext;
  logger: AppDependencies['logger'];
}) {
  const [isSuccess] = result;
  if (isSuccess) {
    logger.info('Successfully coerced client video platform', logContext);
    return;
  }
  const [, error] = result;
  logger.error('Failed to coerce client video platform', logContext, error);
}

const NOOP_RESULT = [true, 'noop'] as const;

export type UseGracefullyCoerceVisitorVideoPlatformCallbackOptions = {
  /**
   * Dependency injection. Defaults to {@link useBaseCoerceVideoPlatform}.
   */
  useCoerceVideoPlatform?: typeof useBaseCoerceVideoPlatform;
};

export function useGracefullyCoerceVisitorVideoPlatformCallback({
  useCoerceVideoPlatform = useBaseCoerceVideoPlatform,
}: UseGracefullyCoerceVisitorVideoPlatformCallbackOptions = {}) {
  const getVideoPlatform = useVideoPlatformCallback();
  const coerceVideoPlatform = useCoerceVideoPlatform();
  const getVideoPlatformOfAdmittedRoom = useGetVideoPlatformOfAdmittedRoomCallback();
  const { logger } = useAppDependenciesContext();

  return useCallback<() => Promise<CoerceVisitorVideoPlatformResult>>(async () => {
    const ownVideoPlatform = normalizeVideoPlatform(getVideoPlatform());
    const videoPlatformOfAdmittedRoom = getVideoPlatformOfAdmittedRoom();
    const logContext: LogContext = {
      fromPlatform: ownVideoPlatform,
      toPlatform: videoPlatformOfAdmittedRoom,
    };

    if (!videoPlatformOfAdmittedRoom) {
      logger.warn('Skipping coercion: no video platform detected for admitted room', logContext);
      return NOOP_RESULT;
    }

    if (videoPlatformsMatch(ownVideoPlatform, videoPlatformOfAdmittedRoom)) {
      return NOOP_RESULT;
    }

    logger.info('Coercing client video platform', logContext);
    const stopwatch = Stopwatch.start();
    const result: CoerceVisitorVideoPlatformResult = await coerceVideoPlatform(videoPlatformOfAdmittedRoom)
      .then(() => [true, videoPlatformOfAdmittedRoom] as const)
      .catch((error: Error) => [false, error] as const);
    logResult({
      result,
      logContext: { ...logContext, durationMs: stopwatch.stop() },
      logger,
    });
    return result;
  }, [getVideoPlatform, getVideoPlatformOfAdmittedRoom, logger, coerceVideoPlatform]);
}
